import { uiSurface } from "shared/ui/uiSurface"
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material"
import { FC, useEffect, useState } from "react"
import { useForm, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  useCreateOrUpdateOrderLayoutMutation,
  useLazyGetOrderLayoutConfigQuery,
} from "src/app/apis"
import { OrderCard } from "src/app/components"
import { useAppSelector } from "src/app/hooks"
import { selectMe } from "src/features/profile"
import {
  SelectWrapper,
  SwitchWrapper,
} from "src/features/shared-module/components"
import {
  defaultOrderLayoutFormValue,
  DeliveryConstraintEnum,
  orderLayoutFormFields,
  orderLayoutOptionsList,
} from "../constants"
import { OrderCardLayoutConfig } from "../models"

export const OrderLayoutConfigForm: FC = () => {
  const { t } = useTranslation()

  const [cardPreview, setCardPreview] = useState(false)
  const [deliveryConstraintSwitch, setDeliveryConstraintSwitch] = useState(true)

  const { control, handleSubmit, getValues, setValue, reset } =
    useForm<OrderCardLayoutConfig>({
      defaultValues: defaultOrderLayoutFormValue,
    })

  const formValue = useWatch({ control }) as OrderCardLayoutConfig
  const usedOptions = [
    formValue.topLeft,
    formValue.topRight,
    formValue.bottomLeft,
    formValue.bottomRight,
  ]

  const optionList = orderLayoutOptionsList.map((e) => {
    return {
      label: t(`orderLayoutOptions.${e}`),
      value: e,
      disabled: usedOptions.includes(e),
    }
  })

  const me = useAppSelector(selectMe)

  const [getOrderLayoutConfigFn] = useLazyGetOrderLayoutConfigQuery({})
  const [upsertOrderLayoutFn] = useCreateOrUpdateOrderLayoutMutation()

  useEffect(() => {
    if (!me?.user.userId) return

    const userId = me.user.userId
    setValue("id", userId)
    const getOrderConfig = async () => {
      const { data: orderLayoutDetail } = await getOrderLayoutConfigFn(userId)

      if (orderLayoutDetail?.data) {
        reset(orderLayoutDetail.data)
        setDeliveryConstraintSwitch(
          orderLayoutDetail.data.deliveryConstraint ===
            DeliveryConstraintEnum.PROGRESS_BAR,
        )
      }
    }
    getOrderConfig()
  }, [getOrderLayoutConfigFn, me?.user.userId, setValue, reset])

  useEffect(() => {
    setValue(
      "deliveryConstraint",
      deliveryConstraintSwitch
        ? DeliveryConstraintEnum.PROGRESS_BAR
        : DeliveryConstraintEnum.TEXT_VERSION,
    )
  }, [deliveryConstraintSwitch, setValue])

  const onSubmit = (formValue: OrderCardLayoutConfig) => {
    upsertOrderLayoutFn(formValue)
  }

  return (
    <>
      <Card
        {...uiSurface("UI-DSP-051")}
        data-testid="config-order-layout-card"
        sx={(theme) => ({
          borderRadius: 3,
          width: 630,
        })}
        elevation={2}
      >
        <CardHeader
          disableTypography
          sx={() => ({
            padding: 2,
          })}
          title={
            <Typography color="textPrimary" variant="body1" fontWeight={600}>
              Order Card Layout
            </Typography>
          }
        />
        <Divider />
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent
            sx={(theme) => ({
              borderColor: theme.palette.primary.light,
              // backgroundColor: theme.palette.grey[50],
            })}
          >
            <Box>
              <Box marginBottom={2}>
                <Stack
                  spacing={1}
                  direction={"row"}
                  justifyContent={"flex-end"}
                  alignItems={"center"}
                >
                  <Typography variant="h5">
                    Delivery Constraint View:
                  </Typography>

                  <Stack direction={"row"} alignItems={"center"}>
                    <Typography
                      variant="body1"
                      color={
                        !deliveryConstraintSwitch ? "primary" : "textPrimary"
                      }
                    >
                      Text View
                    </Typography>
                    <SwitchWrapper
                      data-testid="config-delivery-constraint-switch"
                      value={deliveryConstraintSwitch}
                      onChange={(value: boolean) => {
                        setDeliveryConstraintSwitch(value)
                      }}
                      name={"delivery-constraint-switch"}
                    />
                    <Typography
                      variant="body1"
                      color={
                        deliveryConstraintSwitch ? "primary" : "textPrimary"
                      }
                    >
                      Progress Bar
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
              <Stack spacing={2}>
                <Stack direction={"row"} spacing={2}>
                  <SelectWrapper
                    data-testid="config-top-left-select"
                    size="small"
                    value={getValues("topLeft")}
                    label={t("topLeft")}
                    name={orderLayoutFormFields.topLeft}
                    options={optionList}
                    control={control}
                  />
                  <SelectWrapper
                    data-testid="config-top-right-select"
                    size="small"
                    value={getValues("topRight")}
                    label={t("topRight")}
                    name={orderLayoutFormFields.topRight}
                    options={optionList}
                    control={control}
                  />
                </Stack>
                <Stack direction={"row"} spacing={2}>
                  <SelectWrapper
                    data-testid="config-bottom-left-select"
                    size="small"
                    value={getValues("bottomLeft")}
                    label={t("bottomLeft")}
                    name={orderLayoutFormFields.bottomLeft}
                    options={optionList}
                    control={control}
                  />
                  <SelectWrapper
                    data-testid="config-bottom-right-select"
                    size="small"
                    value={getValues("bottomRight")}
                    label={t("bottomRight")}
                    name={orderLayoutFormFields.bottomRight}
                    options={optionList}
                    control={control}
                  />
                </Stack>
              </Stack>
            </Box>
            <Collapse
              {...uiSurface("UI-DSP-052")}
              in={cardPreview}
              timeout="auto"
              unmountOnExit
              sx={{
                marginTop: 2,
              }}
            >
              <OrderCard
                order={staticOrder}
                orderCardLayoutConfig={formValue}
              />
            </Collapse>
          </CardContent>
          <Divider />
          <CardActions
            sx={{
              margin: 1,
              justifyContent: "flex-end",
            }}
          >
            <Stack direction={"row"} spacing={1} justifyContent={"flex-end"}>
              <Link
                data-testid="config-preview-link"
                color="secondary"
                type="button"
                variant="body2"
                component="button"
                onClick={() => setCardPreview(!cardPreview)}
              >
                {cardPreview ? "Hide card preview" : "View card preview"}
              </Link>
              <Button data-testid="config-save-btn" variant="contained" type="submit">
                Save
              </Button>
            </Stack>
          </CardActions>
        </form>
      </Card>
    </>
  )
}

const staticOrder: any = {
  id: 8436,
  version: 2423934953608662,
  amount: 3462068821960558,
  callTime: null,
  clientId: 18089,
  deliveryDate: "2024-10-06T15:22:37.922Z",
  desiredArrivalTime: "19:34",
  haulerEmployeeId: null,
  notes: null,
  paymentType: null,
  reference: "250322-001",
  state: "READY_FOR_HAULAGE",
  stateMachineWorkflow: "DELIVERY",
  collectedAmount: null,
  addressId: 1894,
  addressSource: null,
  tenantId: 102,
  recurrenceSourceId: null,
  source: "MANUALLY_CREATED",
  recurringOrderPlanifiedDate: null,
  collectingAccount: null,
  sequence: null,
  routeSummaryId: null,
  desiredArrivalTimeHour: null,
  desiredArrivalTimeHourEnd: "18:00",
  calculatedArrivalTime: "2011-10-05T11:00:00.000Z",
  recipientIdentity: null,
  recipientRelation: null,
  recipientRelationMore: null,
  manifestId: null,
  dateCreated: "2024-10-06T15:22:37.922Z",
  lastUpdated: "2024-10-06T15:22:37.922Z",
  encryptedNotes: null,
  externalReference: "1",
  previousCalculatedArrivalTime: null,
  totalAmount: null,
  token: null,
  orderCreatorDataConfigurationId: null,
  orderCreatorDataIntegrationOrderId: null,
  rxProId: null,
  metadata: null,
  address: {
    id: 22086,
    version: 0,
    appartNumber: null,
    city: "Sherbrooke",
    country: "Canada",
    notes: null,
    positionId: 22082,
    postcode: "E1W 9N8",
    state: "NT",
    streetName: "3943 Kingsway",
    streetNumber: "7618",
    routeFromProviderId: 22085,
    tenantId: 102,
    addressOneLine: null,
    label: "Home",
    dateCreated: "2025-03-22T20:40:41.579+00:00",
    lastUpdated: "2025-03-22T20:40:41.579+00:00",
    encryptedNotes: null,
    externalReference: null,
    lockerIntegrationConfigurationId: null,
    lockerIntegrationLockerId: null,
    lockerIntegrationDescription: null,
    lockerIntegrationCommunicationMode: null,
    rxProId: null,
    originalAddress: null,
    position: {
      id: 22082,
      version: 0,
      accuracy: null,
      dateEmitted: "2025-02-21T18:08:02.110Z",
      latitude: 64.804857,
      longitude: -99.883762,
    },
    route: {
      id: 22085,
      version: 0,
      distanceKm: 2.928050318223869,
      duration:
        "\\xaced00057372000d6a6176612e74696d652e536572955d84ba1b2248b20c00007870770d0100000000000000a70000000078",
      fromId: 22083,
      toId: 22084,
      tenantId: 102,
      lastUpdate: "2025-03-22T20:37:12.377+00:00",
      valid: true,
    },
  },
  haulerEmployee: null,
  client: {
    id: 22249,
    version: 0,
    dateCreated: "2025-04-16T20:17:20.807+00:00",
    firstName: "Brock",
    lastName: "Byers",
    lastUpdated: "2025-04-16T20:17:20.807+00:00",
    notes: null,
    tenantId: 102,
    archived: false,
    includesInSurvey: true,
    includesInTakeInChargeNotifications: false,
    userId: null,
    organisation: null,
    driverOwnedClientDetailId: null,
    encryptedNotes: null,
    externalReference: null,
    email: null,
    language: null,
    token: null,
    rxProId: null,
    lastCompletedOrderDate: null,
    tags: [
      {
        clientTagsId: 22249,
        tagId: 201,
        tag: {
          id: 201,
          version: 0,
          archived: false,
          label: "tag.label.callbeforedelivery",
          type: "CLIENT",
          color: "#5BC0DE",
          icon: "phone",
          system: true,
          tenantId: 102,
          displayOption: "ICON",
          onMobile: true,
          shortLabel: null,
          invisibleToHauler: false,
          dateCreated: null,
          lastUpdated: null,
        },
      },
    ],
  },
  routeSummary: null,
  bags: [],
  tags: [
    {
      orderTagsId: 22793,
      tagId: 275,
      tag: {
        id: 275,
        version: 0,
        archived: false,
        label: "tag.label.urgent",
        type: "ORDER",
        color: "#D9534F",
        icon: "exclamation-triangle",
        system: true,
        tenantId: 102,
        displayOption: "ICON",
        onMobile: true,
        shortLabel: null,
        invisibleToHauler: false,
        dateCreated: null,
        lastUpdated: null,
      },
    },
  ],
  oneLineAddress: "7618, 3943 Kingsway, Sherbrooke, E1W 9N8",
  allTagList: [
    {
      id: 201,
      version: 0,
      archived: false,
      label: "tag.label.callbeforedelivery",
      type: "CLIENT",
      color: "#5BC0DE",
      icon: "phone",
      system: true,
      tenantId: 102,
      displayOption: "ICON",
      onMobile: true,
      shortLabel: null,
      invisibleToHauler: false,
      dateCreated: null,
      lastUpdated: null,
    },
    {
      id: 275,
      version: 0,
      archived: false,
      label: "tag.label.urgent",
      type: "ORDER",
      color: "#D9534F",
      icon: "exclamation-triangle",
      system: true,
      tenantId: 102,
      displayOption: "ICON",
      onMobile: true,
      shortLabel: null,
      invisibleToHauler: false,
      dateCreated: null,
      lastUpdated: null,
    },
  ],
  isOnTimeDelivery: true,
}
